import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useApiClient } from "./use-api"

export const instanceKeys = {
  members: ["instance", "members"] as const,
  tags: ["instance", "tags"] as const,
  templates: ["instance", "templates"] as const
}

export function useInstanceMembers() {
  const api = useApiClient()
  return useQuery({
    queryKey: instanceKeys.members,
    queryFn: () => api("/members")
  })
}

export function useInstanceTags() {
  const api = useApiClient()
  return useQuery({
    queryKey: instanceKeys.tags,
    queryFn: () => api("/tags")
  })
}

export function useInviteInstanceMember() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role: "admin" | "member" }) =>
      api("/members/invite", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.members })
    }
  })
}

export function useUpdateInstanceMemberRole() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { memberId: string; role: "admin" | "member" }) =>
      api(`/members/${data.memberId}`, {
        method: "PATCH",
        body: JSON.stringify({ role: data.role })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.members })
    }
  })
}

export function useRemoveInstanceMember() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) => api(`/members/${memberId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.members })
    }
  })
}

export function useCreateInstanceTag() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; color: string }) => api("/tags", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.tags })
    }
  })
}

export function useUpdateInstanceTag() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { tagId: string; name?: string; color?: string }) =>
      api(`/tags/${data.tagId}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...(data.name !== undefined && { name: data.name }),
          ...(data.color !== undefined && { color: data.color })
        })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.tags })
    }
  })
}

export function useDeleteInstanceTag() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tagId: string) => api(`/tags/${tagId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.tags })
    }
  })
}

// --- Module Templates ---

export function useInstanceTemplates() {
  const api = useApiClient()
  return useQuery({
    queryKey: instanceKeys.templates,
    queryFn: () => api("/module-templates?limit=100")
  })
}

export function useCreateInstanceTemplate() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; type: string; schema: string; version?: number }) =>
      api("/module-templates", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.templates })
    }
  })
}

export function useUpdateInstanceTemplate() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { templateId: string; name?: string; schema?: string; version?: number }) => {
      const { templateId, ...body } = data
      return api(`/module-templates/${templateId}`, {
        method: "PATCH",
        body: JSON.stringify(body)
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.templates })
    }
  })
}

export function useDeleteInstanceTemplate() {
  const api = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (templateId: string) => api(`/module-templates/${templateId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: instanceKeys.templates })
    }
  })
}
